import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { LoginFormModel } from 'src/models/loginModel';
import { removeItem, errorMsg, setItem } from '../utilities/utils';
import ToastMessage from 'src/components/ToastContainer';
import { useLoginActions } from 'src/store/login/actions';
import { RootState } from 'src/store/reducers/state';
import FormFieldError from '../common/formFieldError';
import * as AppConstants from '../utilities/constants';
import { useSearchParams } from 'react-router-dom';
import Select from 'react-select';

// Prevent search engine indexing
const NoIndexMeta = () => {
    useEffect(() => {
        const meta = document.createElement('meta');
        meta.name = 'robots';
        meta.content = 'noindex, nofollow';
        document.head.appendChild(meta);
        return () => { document.head.removeChild(meta); };
    }, []);
    return null;
};

type AdminLoginStep = 'credentials' | 'domain-select';

const DOMAIN_SELECT_RETURN = '/platform-login?step=domain-select';

const AdminLogin: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const loginAction = useLoginActions(dispatch);
    const [searchParams] = useSearchParams();

    const stepParam = searchParams.get('step');
    const [step, setStep] = useState<AdminLoginStep>(
        stepParam === 'domain-select' ? 'domain-select' : 'credentials'
    );

    const [selectedDomainId, setSelectedDomainId] = useState<string>('');

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm<LoginFormModel>();

    const {
        loginError,
        loading,
        isLoggedIn,
        isCallbackLink,
        superAdminDomains,
        defaultDateFormat,
        domainTokenLoading,
        domainToken
    } = useSelector((state: RootState) => ({
        loginError: state.login.error,
        loading: state.login.loading,
        isLoggedIn: state.login.isLoggedIn,
        isCallbackLink: state.login.isCallbackLink,
        superAdminDomains: state.login.superAdminDomains,
        defaultDateFormat: state.login.defaultDateFormat,
        domainTokenLoading: state.login.domainTokenLoading,
        domainToken: state.login.domainToken
    }));

    useEffect(() => {
        if (step === 'domain-select') {
            loginAction.getSuperAdminDomainsRequest();
        }
    }, [step]);

    const handleAdminLogin = (data: LoginFormModel) => {
        loginAction.sendLoginRequest(data);
    };

    useEffect(() => {
        if (isLoggedIn) {
            loginAction.getDefaultdateFormatRequest();
        }
    }, [isLoggedIn]);

    useEffect(() => {
        if (isLoggedIn && Object.keys(defaultDateFormat).length && !defaultDateFormat?.accountRestricted) {
            const localData = localStorage.getItem('userData');
            const userData = localData && JSON.parse(localData);
            const isSA = userData?.role === 'Super Admin';
            if (userData?.role !== 'Super Admin') {
                removeItem('token');
                removeItem('role');
                removeItem('userData');
                removeItem('accountInformation');
                removeItem('refreshToken');
                removeItem('defaultDateFormat');
                sessionStorage.clear();
                loginAction.resetLoginStateRequest();
                errorMsg('Access denied. This portal is for Webcastcloud Super Admins only.');
                return;
            }

            if (isSA) {
                redirectToMfa(userData, DOMAIN_SELECT_RETURN);
            } else {
                redirectToMfa(userData, '/');
            }

            loginAction.resetLoginStateRequest();
        }
    }, [isLoggedIn, defaultDateFormat]);

    const redirectToMfa = (userData: any, returnUrl: string) => {
        if (userData?.isMfaSetupEnabled) {
            const authToken = localStorage.getItem('token');
            if (authToken) {
                sessionStorage.setItem(
                    'pendingMfaAuth',
                    JSON.stringify({ token: authToken, userData, timestamp: Date.now() })
                );
                localStorage.removeItem('token');
                localStorage.removeItem('role');
                localStorage.removeItem('userData');
            }
            navigate(
                returnUrl === '/'
                    ? '/account/CodeVerification'
                    : `/account/CodeVerification?returnUrl=${encodeURIComponent(returnUrl)}`
            );
        } else {
            navigate(
                returnUrl === '/'
                    ? '/account/MultiFactorAuthentication'
                    : `/account/MultiFactorAuthentication?returnUrl=${encodeURIComponent(returnUrl)}`
            );
        }
    };

    const handleDomainContinue = () => {
        if (!selectedDomainId) {
            errorMsg('Please select a domain to continue.');
            return;
        }
        loginAction.getDomainTokenRequest({ DomainId: selectedDomainId });
    };

    useEffect(() => {
        if (Object.keys(domainToken).length) {
            localStorage.removeItem('refreshToken');
            setItem('token', domainToken.authToken ?? domainToken.AuthToken);
            localStorage.setItem('activeDomainId', selectedDomainId);
            localStorage.setItem('activeDomainName', domainToken.DomainName);
            localStorage.setItem('refreshToken', domainToken.RefreshToken);
            navigate('/');
        }
    }, [domainToken]);

    useEffect(() => {
        if (loginError && Object.keys(loginError).length) {
            errorMsg(
                loginError.customerror ? loginError.error_description : 'Something went wrong. Please try again.'
            );
        }
    }, [loginError]);

    const Spinner = () => (
        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
        </svg>
    );

    return (
        <div>
            <NoIndexMeta />
            <ToastMessage />

            <section
                className="bg-gray-100 h-screen"
                style={{
                    backgroundImage: `url(../../assets/img/wcc-background-image.png)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                }}>
                <img className="w-64 absolute top-4 left-4" src="../../assets/img/wcc-logo.png" alt="logo" />

                <div className="container px-6 h-full mx-auto">
                    <div className="flex justify-center items-center h-full">
                        <div className="xl:w-3/5 lg:w-3/5 md:w-8/12 w-12/12">
                            <div className="block bg-white shadow-lg rounded-xl">
                                <div className="lg:flex-wrap g-0">
                                    <div className="px-4 md:px-0">
                                        <div className="md:px-8 px-5 py-16">


                                            {step === 'credentials' && (
                                                <>
                                                    <div className="mb-6">
                                                        <div className="flex justify-center mb-1">
                                                            <h1 className="font-black text-2xl">Webcastcloud Platform Console</h1>
                                                        </div>
                                                    </div>

                                                    {defaultDateFormat?.accountRestricted && (
                                                        <p className="text-red-600 text-sm text-center mb-4 font-bold">
                                                            Account restrictions apply.
                                                        </p>
                                                    )}

                                                    <form onSubmit={handleSubmit(handleAdminLogin)}>
                                                        <div className="mb-4">
                                                            <label className="mb-1 block font-bold">Work Email</label>
                                                            <input
                                                                type="text"
                                                                className="input-style"
                                                                id="UserName"
                                                                {...register('UserName', {
                                                                    required: true,
                                                                    maxLength: 200,
                                                                    pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
                                                                })}
                                                            />
                                                            {errors.UserName?.type === 'required' && <FormFieldError message={AppConstants.requiredField} />}
                                                            {errors.UserName?.type === 'pattern' && <FormFieldError message={AppConstants.notValidEmail} />}
                                                            {errors.UserName?.type === 'maxLength' && <FormFieldError message={AppConstants.maxLengthReached} />}
                                                        </div>

                                                        <div className="mb-4">
                                                            <label className="mb-1 block font-bold">Password</label>
                                                            <input
                                                                id="Password"
                                                                type="password"
                                                                className="input-style"
                                                                {...register('Password', { required: true })}
                                                            />
                                                            {errors.Password?.type === 'required' && <FormFieldError message={AppConstants.requiredField} />}
                                                        </div>

                                                        <div className="mb-4 mt-6">
                                                            <button
                                                                className="btn-primary m-0 w-full px-16"
                                                                type="submit"
                                                                disabled={loading}
                                                                style={{ backgroundColor: '#6638ff', borderColor: '#6638ff' }}>
                                                                {loading ? (
                                                                    <span className="flex items-center justify-center gap-2">
                                                                        <Spinner />
                                                                        Signing in...
                                                                    </span>
                                                                ) : 'Sign In'}
                                                            </button>
                                                        </div>
                                                    </form>
                                                </>
                                            )}


                                            {step === 'domain-select' && (
                                                <>
                                                    <div className="mb-6">
                                                        <div className="flex justify-center mb-1">
                                                            <h1 className="font-black text-2xl">Select Client Domain</h1>
                                                        </div>
                                                        <p className="text-center text-gray-500 text-sm">
                                                            Choose the domain you want to manage
                                                        </p>
                                                    </div>

                                                    <div className="mb-6">
                                                        <label className="mb-1 block font-bold">Client Domain</label>

                                                        {!superAdminDomains || superAdminDomains.length === 0 ? (
                                                            <div className="flex items-center justify-center py-6 text-gray-400 gap-2">
                                                                <Spinner />
                                                                <span className="text-sm">Loading domains...</span>
                                                            </div>
                                                        ) : (
                                                            <Select
                                                                options={superAdminDomains.map((domain: { DomainId: string; DomainName: string }) => ({
                                                                    value: domain.DomainId,
                                                                    label: domain.DomainName
                                                                }))}
                                                                value={
                                                                    selectedDomainId
                                                                        ? {
                                                                            value: selectedDomainId,
                                                                            label: superAdminDomains.find(
                                                                                (d: any) => d.DomainId === selectedDomainId
                                                                            )?.DomainName
                                                                        }
                                                                        : null
                                                                }
                                                                onChange={(option) => setSelectedDomainId(option?.value ?? '')}
                                                                placeholder="-- Select a domain --"
                                                                classNamePrefix="react-select"
                                                                styles={{
                                                                    control: (base, state) => ({
                                                                        ...base,
                                                                        borderColor: state.isFocused ? '#6638ff' : '#d1d5db',
                                                                        boxShadow: state.isFocused ? '0 0 0 3px rgba(102,56,255,0.15)' : 'none',
                                                                        '&:hover': { borderColor: '#6638ff' },
                                                                        borderRadius: '0.5rem',
                                                                        minHeight: '42px'
                                                                    }),
                                                                    option: (base, state) => ({
                                                                        ...base,
                                                                        backgroundColor: state.isSelected ? '#6638ff' : state.isFocused ? '#f3f0ff' : 'white',
                                                                        color: state.isSelected ? 'white' : '#111827'
                                                                    })
                                                                }}
                                                            />
                                                        )}
                                                    </div>



                                                    <div className="mb-4 mt-4 flex gap-3">
                                                        <button
                                                            className="flex items-center justify-center gap-2 w-1/3 px-4 py-2 rounded-lg border border-gray-300 text-gray-600 font-medium hover:bg-gray-50 hover:border-gray-400 transition-all"
                                                            type="button"
                                                            onClick={() => {

                                                                removeItem('token');
                                                                removeItem('userData');
                                                                removeItem('role');
                                                                sessionStorage.clear();
                                                                navigate('/platform-login');
                                                            }}>
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                                            </svg>
                                                            Back
                                                        </button>

                                                        <button
                                                            className="btn-primary w-2/3 px-4 flex items-center justify-center gap-2"
                                                            type="button"
                                                            disabled={!selectedDomainId || domainTokenLoading}
                                                            onClick={handleDomainContinue}
                                                            style={{ backgroundColor: '#6638ff', borderColor: '#6638ff' }}>
                                                            {domainTokenLoading ? (
                                                                <>
                                                                    <Spinner />
                                                                    Loading...
                                                                </>
                                                            ) : 'Continue to Admin Portal'}
                                                        </button>
                                                    </div>
                                                </>
                                            )}

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AdminLogin;